import { useEffect, useRef } from 'react';
import { appContext } from '../app/context';
import { commitTextDraft } from '../app/textFlow';
import { domBaselineOffset, draftFont, fontMetrics } from '../io/fontMetrics';
import { useEditorStore } from '../state/editorStore';

/**
 * On-canvas text editing.
 *
 * A textarea laid over the canvas at the text's own position and scale. Doing it
 * in the DOM rather than inside Fabric keeps IME input, selection and caret
 * behaviour native, and keeps the engine a pure renderer.
 *
 * The draft is committed once, when editing ends — so typing a paragraph is one
 * undo step, and abandoning it leaves no layer behind.
 */
export function TextEditorOverlay({
  stageRef,
}: {
  stageRef: React.RefObject<HTMLDivElement | null>;
}) {
  const draft = useEditorStore((s) => s.textDraft);
  const viewport = useEditorStore((s) => s.viewport);
  const setTextDraft = useEditorStore((s) => s.setTextDraft);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);

  const finish = (commit: boolean) => {
    const current = useEditorStore.getState().textDraft;
    setTextDraft(null);
    if (current && commit) commitTextDraft(appContext, current);
  };

  // Identity of the open editing session: a new draft re-focuses, typing does
  // not yank the caret back to the end.
  const openKey = draft ? `${draft.layerId ?? 'new'}:${draft.x}:${draft.y}` : null;

  useEffect(() => {
    if (!openKey) return;
    const area = areaRef.current;
    if (!area) return;
    area.focus();
    area.setSelectionRange(area.value.length, area.value.length);
  }, [openKey]);

  if (!draft) return null;

  const stage = stageRef.current;
  const stageRect = stage?.getBoundingClientRect();
  // The draft draws with the model's line height and the model's first
  // baseline (lineHeight/2 below the box top) — the same rule the bake, the
  // export and the merges use, so committing moves no glyphs. A DOM textarea
  // puts its first baseline wherever CSS half-leading does, so the element is
  // shifted by the measured difference (`domBaselineOffset`; falls back to no
  // shift where font metrics are unavailable, rather than a wrong one).
  const metrics = fontMetrics(draftFont(draft, draft.fontSize));
  const baselineShift = (metrics ? domBaselineOffset(metrics) : 0) * viewport.zoom;
  const left = (stageRect?.left ?? 0) + draft.x * viewport.zoom + viewport.pan.x;
  const top = (stageRect?.top ?? 0) + draft.y * viewport.zoom + viewport.pan.y + baselineShift;

  return (
    <textarea
      ref={areaRef}
      data-testid="text-editor"
      value={draft.text}
      spellCheck={false}
      onChange={(event) => setTextDraft({ ...draft, text: event.target.value })}
      onBlur={() => finish(true)}
      onKeyDown={(event) => {
        // An IME owns Escape while a composition is in flight — cancelling a
        // CJK candidate window must not discard the whole draft with it.
        if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return;
        if (event.key === 'Escape') {
          event.stopPropagation();
          finish(false);
        }
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          finish(true);
        }
        // Do not let single-key tool shortcuts fire while typing.
        event.stopPropagation();
      }}
      style={{
        position: 'fixed',
        left,
        top,
        minWidth: `${160 * viewport.zoom}px`,
        minHeight: `${draft.fontSize * viewport.zoom}px`,
        fontSize: `${draft.fontSize * viewport.zoom}px`,
        // The model's leading, not a look-alike: the committed layer uses it,
        // and the baseline maths above assumes it.
        lineHeight: draft.lineHeight,
        fontFamily: draft.fontFamily,
        fontWeight: draft.bold ? 'bold' : 'normal',
        fontStyle: draft.italic ? 'italic' : 'normal',
        color: draft.color,
        caretColor: 'var(--color-accent)',
        background: 'rgba(255,255,255,0.06)',
        // An outline, not a border: a border shifts the content box down by
        // its width and the baseline maths off by the same pixel.
        outline: '1px dashed var(--color-accent)',
        border: 'none',
        padding: 0,
        margin: 0,
        resize: 'none',
        overflow: 'hidden',
        zIndex: 30,
      }}
    />
  );
}
